import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import PageHeader from "@core/components/pageHeader";
import MembershipPriceList from "views/pages/membership/MembershipPriceList";
import MembershipPriceForm from "views/pages/membership/MembershipPriceForm";
import APICall from "network/APICall";
import EndPoints from "network/Endpoints";
import { useLocation } from "react-router-dom";
import Utility from "@core/helper/utility";
import { useSelector } from "react-redux";
import Loader from "@core/helper/Loader";

/**
 * Membership Pricing page
 */
const MembershipPricing = () => {
  const [openModal, setOpenModal] = useState(false);
  const [formType, setFormType] = useState("add");
  const [activePrice, setActivePrice] = useState(null);
  const [priceList, setPriceList] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalRecord, setTotalRecord] = useState(0);
  const [permission, setPermission] = useState({});
  const [role, setRole] = useState("");

  const { userData } = useSelector((state) => state.auth);
  const location = useLocation();

  useEffect(() => {
    if (userData?.data?.user?.role === "2") {
      const membership = Utility.getUserData({
        datastore: userData,
        activePath: location.pathname,
      });
      setPermission(membership[1] || {});
    } else {
      setPermission({});
    }
    setRole(userData?.data?.user?.role || "");
  }, [location.pathname, userData]);

  useEffect(() => {
    fetchPriceHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, pageNumber]);

  const fetchPriceHistory = () => {
    Loader.isLoading(true);
    const payload = { pageNo: pageNumber + 1, limit: pageSize };
    APICall("post", payload, EndPoints.getMembershipPrices)
      .then((response) => {
        if (response.status === 200 && response.data?.data) {
          const { response: rows, totalCount } = response.data.data;
          setPriceList(rows || []);
          setTotalRecord(totalCount || 0);
          const active = (rows || []).find((r) => r.is_active);
          setActivePrice(active || null);
        } else {
          setPriceList([]);
        }
      })
      .catch(() => toast.error("Failed to load membership prices"))
      .finally(() => Loader.isLoading(false));
  };

  const handleEdit = (row) => {
    if (row) {
      setActivePrice(row);
      setFormType("edit");
    } else {
      setFormType("add");
    }
    setOpenModal(true);
  };

  const handleFormClose = () => {
    setOpenModal(false);
  };

  const handleMembershipStatusChange = (id) => {
    Loader.isLoading(true);
    APICall("post", { id }, EndPoints.changeMembershipPriceStatus)
      .then((response) => {
        const { isError, message } = response.data;
        if (!isError) {
          toast.success(message);
          fetchPriceHistory();
        } else {
          toast.error(message);
        }
      })
      .catch(() => { })
      .finally(() => Loader.isLoading(false));
  };

  return (
    <Grid container spacing={4}>
      <PageHeader
        title={
          <Typography variant="body1" sx={{ fontSize: "1.3rem !important" }}>
            Membership Pricing
          </Typography>
        }
      />
      {((permission.view && role === "2") || role === "1") && (
        <Grid item xs={12}>
          <MembershipPriceList
            priceList={priceList}
            pageSize={pageSize}
            pageNumber={pageNumber}
            totalRecord={totalRecord}
            setPageSize={setPageSize}
            setPageNumber={setPageNumber}
            handleEdit={handleEdit}
            handleMembershipStatusChange={handleMembershipStatusChange}
            permission={permission}
            role={role}
          />
        </Grid>
      )}
      <MembershipPriceForm
        open={openModal}
        toggle={handleFormClose}
        type={formType}
        heading={
          formType === "edit"
            ? "Edit Membership Price"
            : "Add Membership Price"
        }
        activePrice={activePrice}
        onSuccess={fetchPriceHistory}
      />
    </Grid>
  );
};

export default MembershipPricing;
